import { useState, useRef, useEffect } from 'react';
import { useTheme, type Theme } from '@/lib/theme';
import { cn } from '@/lib/utils';

const OPTIONS: Array<{ value: Theme; label: string; icon: string }> = [
    { value: 'light',  label: 'Claro',    icon: '☀️' },
    { value: 'dark',   label: 'Oscuro',   icon: '🌙' },
    { value: 'system', label: 'Sistema',  icon: '🖥️' },
];

export function ThemeToggle() {
    const { theme, resolved, setTheme } = useTheme();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        function onClick(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        }
        function onKey(e: KeyboardEvent) {
            if (e.key === 'Escape') setOpen(false);
        }
        document.addEventListener('mousedown', onClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const currentIcon = resolved === 'dark' ? '🌙' : '☀️';

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                aria-label="Cambiar tema"
                aria-expanded={open}
                aria-haspopup="menu"
                className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
                <span className="text-lg" aria-hidden="true">{currentIcon}</span>
            </button>
            {open && (
                <div
                    role="menu"
                    className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-30"
                >
                    {OPTIONS.map(opt => (
                        <button
                            key={opt.value}
                            role="menuitemradio"
                            aria-checked={theme === opt.value}
                            onClick={() => { setTheme(opt.value); setOpen(false); }}
                            className={cn(
                                'w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors',
                                theme === opt.value
                                    ? 'bg-brand-blue/10 text-brand-blue dark:bg-brand-blue/20 font-medium'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                            )}
                        >
                            <span aria-hidden="true">{opt.icon}</span>
                            <span>{opt.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
